import React from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Footer } from '../Components/Footer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenRoles from '../Components/about-page/open-roles';
import { styled } from '@mui/material/styles';
import FreeTrial from '../Components/about-page/free-trial';
import AboutHeroSection from '../Components/about-page/hero-section';
import ValuesSlider from '../Components/about-page/our-values';
import OrangeButton from '../Components/buttons/orange-button';
import { useNavigate } from 'react-router-dom';
import HeaderAlt from '../Components/HeaderAlt';
const About = () => {
  const navigate = useNavigate();
  const StyledSection = styled('section')({
    backgroundColor: '#e6f7ff',
    padding: '60px 0',
    textAlign: 'center',
    width: '100%',
  });

  const TeamCard = styled(Card)({
    maxWidth: 200,
    margin: '0 auto',
    backgroundColor: 'transparent',
    padding: '0',
    boxShadow: 'none',
  });

  const TeamImage = styled('img')({
    width: '100%',
    height: 200,
    objectFit: 'cover',
    borderRadius: '8px',
  });

  const TeamMemberName = styled(Typography)({
    fontSize: '16px',
    fontWeight: 600,
    marginTop: '16px',
    marginBottom: '4px',
  });

  const TeamMemberRole = styled(Typography)({
    fontSize: '14px',
    color: '#666',
  });

  const publicUrl = process.env.PUBLIC_URL;

  const faqData = [
    {
      question: 'What does IAMAI Mean?',
      answer:
        '"IAMAI" stands for "I Am Always Improving," reflecting our commitment to continuous learning and growth.',
    },
    {
      question: 'What problem is IAMAI solving?',
      answer:
        'IAMAI addresses the challenges educators face managing administrative tasks, session preparation, progress tracking, and personalized student support—giving them back valuable time to teach.',
    },
    {
      question: 'What makes IAMAI different?',
      answer: 'IAMAI combines human expertise with advanced AI technology, providing a premium "Concierge" experience that includes personalized learning tools, automated session summaries, student management dashboards, and direct, white-glove support.',
    },
    {
      question: 'How can educators join IAMAI?',
      answer:
        "We're currently onboarding a select group of elite educators for our premium Concierge MVP. Educators interested in becoming founding members can apply here →.",
    },
  ];

  const teamMembers = [
    {
      name: 'John Mason Mings',
      role: 'CEO',
      image: '/images/people-imgs/john.png',
    },
    {
      name: 'Noah Perelmuter',
      role: 'Chief Intern',
      image: '/images/people-imgs/noah.png',
    },
    {
      name: 'Ajay P.',
      role: 'Full Stack Developer',
      image: '/images/people-imgs/ajay.png',
    },
  ];

  return (
    <>
      <HeaderAlt />
      <div className="flex flex-col">
        <AboutHeroSection />
        {/* Mission Section */}
        <div className="container !mb-14 !mt-[59px] md:!mb-[109px]">
          <div className="space-y-[14px] md:space-y-6">
            <h1 className="hero-title fredoka text-center !text-[#012E33] md:text-center">🎯 Our Mission</h1>
            <h3 className="mission-title fredokaOne relative text-center md:text-left">
              Empower educators worldwide with cutting-edge AI tools to deliver personalized and effective learning experiences.
            </h3>
          </div>
        </div>

        {/* Story and FAQ Section */}
        <Box className="container">
          <Grid container spacing={4} alignItems="top">
            <Grid item xs={12} md={6}>
              <Typography
                fontFamily="fredokaOne"
                variant="h4"
                sx={{
                  fontWeight: 'normal',
                  marginBottom: '14x',
                  lineHeight: 1.4,
                  color: '#003b47',
                  fontSize: { xs: '40px', sm: 'inherit' },
                }}
              >
                🚀 Our Story
              </Typography>
              <Typography
                fontFamily="fredoka"
                variant="h5"
                sx={{ color: '#003b47', fontWeight: 'normal', marginBottom: '14px' }}
              >
                IAMAI began in 2023 as a quest to transform learning through AI-driven personalization.
              </Typography>
              <Typography
                fontFamily="fredoka"
                variant="body1"
                sx={{
                  marginBottom: '14px',
                  lineHeight: '32px',
                  letterSpacing: '-0.22px',
                  color: '#678285',
                  fontSize: { xs: '18px', sm: 'inherit' },
                }}
              >
                Founder Mason Mings, an educator passionate about technology and personalized 
                learning, experienced firsthand the struggles faced by students and tutors. 
                Supported by veteran educators and advisors such <span>as <a
                  href="http://localhost:3000/advisor"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-blue-600 hover:text-blue-800"
                >
                  Robin Doran and Bob McPherson
                </a>, </span>
                Mason developed AI-powered tools initially for his own students.
              </Typography>
              <Typography
                fontFamily="fredoka"
                variant="body1"
                sx={{
                  marginBottom: '14px',
                  lineHeight: '32px',
                  letterSpacing: '-0.22px',
                  color: '#678285',
                  fontSize: { xs: '18px', sm: 'inherit' },
                }}
              >
                After seeing the significant impact these tools made on student engagement and 
                performance, Mason recognized that empowering educators directly would create a far 
                greater and more scalable impact.
              </Typography>
              <Typography
                fontFamily="fredoka"
                variant="body1"
                sx={{
                  marginBottom: '14px',
                  lineHeight: '32px',
                  letterSpacing: '-0.22px',
                  color: '#678285',
                  fontSize: { xs: '18px', sm: 'inherit' },
                }}
              >
                Today, IAMAI equips elite tutors with advanced tools for seamless session management,
                automatic student progress tracking, personalized study materials, and detailed 
                session summaries. IAMAI is currently onboarding a select group of exceptional 
                educators to refine our AI-assisted Concierge MVP—a premium, white-glove service 
                designed specifically to free educators from administrative burdens, allowing them to 
                focus fully on teaching and student success.
              </Typography>

              {/* FAQ Accordion */}
              <Box>
                {faqData.map((faq, index) => (
                  <Accordion key={index} sx={{ backgroundColor: '', boxShadow: 'none' }}>
                    <AccordionSummary
                      fontFamily="fredoka"
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ fontWeight: 'normal', color: '#012E33', padding: '0' }}
                    >
                      {faq.question}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        fontFamily="fredoka"
                        fontWeight="400"
                        variant="body2"
                        sx={{ color: '#678285' }}
                      >
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>

            {/* Image Section */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <img
                    src="/images/about/faq-img-1.png"
                    alt="Educators"
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <img
                    src="/images/about/faq-img-2.png"
                    alt="Teaching"
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <img
                    src="/images/about/faq-img-3.png"
                    alt="Classroom"
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <img
                    src="/images/about/faq-img-4.png"
                    alt="Discussion"
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Values Section */}
        <ValuesSlider />

        {/* Team Section */}
        <StyledSection className="!pb-10 !pt-6 md:!pt-[60px]">
          <Container maxWidth="md">
            <Box mb={'30px'} className="!space-y-[14px]">
              <h6 className="fredoka text-center  text-[22px] font-medium leading-[32px] tracking-[-0.22px] text-[#012E33]">
                <span role="img" aria-label="handshake">
                  😎
                </span>{' '}
                Our Team
              </h6>
              <h3 className="fredoka text-center text-[40px] font-semibold leading-tight tracking-[-0.8px] text-[#012E33]">
                Diverse team with a <br />
                single mission
              </h3>
              <p className="text-center font-[Fredoka] text-[22px] font-normal leading-[32px] tracking-[-0.22px] text-[#678285]">
                We have proven that a strong sense of purpose can cross <br /> borders, languages
                and time zones.
              </p>
            </Box>

            <Grid container spacing={2} justifyContent="center">
              {teamMembers.map((member, index) => (
                <Grid className="" item key={index}>
                  <TeamCard>
                    <TeamImage src={publicUrl + member.image} alt={member.name} />
                    <CardContent className="team-member-cont !px-3 !py-4">
                      <TeamMemberName className="text-left font-[Fredoka] text-[22px] font-medium leading-[32px] tracking-[-0.22px] text-[#012E33]">
                        {member.name}
                      </TeamMemberName>
                      <TeamMemberRole className="text-left font-[Fredoka] text-[18px] font-normal leading-[26px] tracking-[-0.36px] text-[#678285]">
                        {member.role}
                      </TeamMemberRole>
                    </CardContent>
                  </TeamCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </StyledSection>

        {/* Open Roles Section */}
        <OpenRoles />

        {/* Free Trial Section */}
        <div className="container">
          <FreeTrial />
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default About;
